import { useMemo } from 'react'
import { Box, Typography, Paper, Grid, Card, CardContent, Avatar, Chip, Button, List, ListItem, ListItemAvatar, ListItemText, Divider, Container, Alert, LinearProgress } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useAppSelector } from '../hooks/redux'
import { useHasRole } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import {
  Folder,
  Groups,
  People,
  Description,
  Assessment,
  VideoCall,
  TrendingUp,
  Notifications,
  Schedule,
  ArrowForward,
  AttachFile,
  BarChart,
  CalendarToday,
  AccessTime,
  CheckCircle,
  ErrorOutline,
  SmartToy,
  EmojiEvents,
  WarningAmber,
  HourglassEmpty,
} from '@mui/icons-material'
import { gradientBrand } from '../theme/brand'
import NextHearingCountdown from '../components/NextHearingCountdown'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery, RoundType } from '../store/api/roundsApi'
import { useGetLatestBracketQuery } from '../store/api/bracketsApi'
import { useGetReportsDashboardQuery, useGetPublicLatestResultsQuery } from '../store/api/reportsApi'
import { useGetMemorialsQuery } from '../store/api/memorialsApi'
import { useGetNotificationsQuery } from '../store/api/notificationsApi'
import { getPriorityRegistrationIssues, getTeamRegistrationSummary } from '../utils/teamRegistration'
import { getClaimantName, formatCaseDisplay } from '../utils/caseDisplay'

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'memorial':
      return <Description color="primary" />
    case 'score':
      return <Assessment color="success" />
    case 'courtroom':
      return <VideoCall color="info" />
    case 'team':
      return <Groups color="secondary" />
    case 'evidence':
      return <AttachFile color="warning" />
    default:
      return <Notifications color="action" />
  }
}

// B.1 — small row component cho block "Phân tích & Báo cáo"
interface AnalyticsRowProps {
  icon: React.ReactNode
  label: string
  value?: number
  valueText?: string
}
function AnalyticsRow({ icon, label, value, valueText }: AnalyticsRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 0.75,
        px: 1,
        borderRadius: 1.5,
        backgroundColor: (t) => alpha(t.palette.action.hover, 0.4),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: 500,
          // #34 (audit P3): label dài không cần wrap nhiều dòng → ellipsis.
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={label}
      >
        {label}
      </Typography>
      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
        {valueText ?? value ?? 0}
      </Typography>
    </Box>
  )
}

/**
 * Block "Giới thiệu cuộc thi" — render cho student thay thế block analytics.
 *
 * Hiển thị thông tin tổng quan các cuộc thi đội đang tham gia:
 *   - Tên + mã cuộc thi
 *   - Vai trò nguyên đơn HS1 + HS2 (case dual)
 *   - Deadline gần nhất
 *
 * Spec PHẦN 4.5 (audit 2026-05-21): student chỉ thấy info, không thấy số liệu
 * tổng hợp toàn hệ thống.
 */
interface CompetitionIntroCardProps {
  userTeam: { id: string; name: string } | null
  cases: Array<{
    id: string
    title: string
    case_number?: string | null
    plaintiff?: string | null
    defendant?: string | null
    scenario_a_claimant_name?: string | null
    scenario_b_claimant_name?: string | null
    assigned_team_ids?: string[] | null
    r1_flow_version?: string | null
    deadline?: string | null
  }>
  onNavigateCases: () => void
  onNavigateDossier: () => void
}
function CompetitionIntroCard({ userTeam, cases, onNavigateCases, onNavigateDossier }: CompetitionIntroCardProps) {
  const teamCases = userTeam
    ? cases.filter((c) => (c.assigned_team_ids || []).includes(userTeam.id))
    : []
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 3,
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
      })}
    >
      <Typography
        variant="h6"
        fontWeight={600}
        gutterBottom
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}
      >
        <Folder color="secondary" /> Giới thiệu cuộc thi
      </Typography>
      {!userTeam || teamCases.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {userTeam
            ? 'Đội của bạn chưa được phân vào cuộc thi nào. BTC sẽ thông báo khi có lịch.'
            : 'Bạn chưa thuộc đội nào. Vui lòng liên hệ thủ quân đội để được thêm vào.'}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {teamCases.map((c) => {
            const isDual = c.r1_flow_version === 'dual_claimant_packets'
            const deadlineStr = c.deadline ? new Date(c.deadline).toLocaleDateString('vi-VN') : null
            return (
              <Box
                key={c.id}
                sx={(t) => ({
                  p: 1.5,
                  borderRadius: 1.5,
                  border: `1px solid ${t.palette.divider}`,
                  backgroundColor: alpha(t.palette.action.hover, 0.4),
                })}
              >
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  {formatCaseDisplay(c as any)}
                </Typography>
                {isDual && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    <Chip
                      size="small"
                      label={`Hồ sơ 1 — ${getClaimantName(c as any, 'scenario_a')}`}
                      sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      label={`Hồ sơ 2 — ${getClaimantName(c as any, 'scenario_b')}`}
                      sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                    />
                  </Box>
                )}
                {deadlineStr && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Deadline: {deadlineStr}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Folder />}
          onClick={onNavigateCases}
          sx={{ textTransform: 'none' }}
        >
          Chi tiết cuộc thi
        </Button>
        {teamCases.length > 0 && (
          <Button
            size="small"
            variant="contained"
            startIcon={<Description />}
            onClick={onNavigateDossier}
            sx={{ textTransform: 'none' }}
          >
            Hồ sơ khởi kiện
          </Button>
        )}
      </Box>
    </Paper>
  )
}

export default function HomePage() {
  const { user } = useAppSelector((state) => state.auth)
  const isAdminQueue = useHasRole(['secretary', 'support'])
  const isStudent = useHasRole(['student'])
  const navigate = useNavigate()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: cases = [] } = useGetCasesQuery()
  const { data: dashboard } = useGetReportsDashboardQuery(undefined, {
    skip: !user || !isAdminQueue,
  })
  // B.1: data cho block "Phân tích & Báo cáo"
  const { data: allMemorials = [] } = useGetMemorialsQuery(
    {},
    { skip: !isAdminQueue },
  )
  // B.1: 4 thông báo mới nhất cho block "Thông báo" admin dashboard
  const { data: recentNotifs = [] } = useGetNotificationsQuery(
    { limit: 4 },
    { skip: !user, refetchOnMountOrArgChange: true },
  )
  const realStats = {
    totalCases: cases.length,
    totalTeams: teams.length,
    totalParticipants: teams.reduce((sum, t) => sum + (t.members?.filter(Boolean).length || 0), 0),
    totalCourtrooms: dashboard?.total_courtrooms ?? 0,
    submittedScoresCount: dashboard?.submitted_scores_count ?? 0,
    averageScore:
      dashboard?.average_total_score != null
        ? Number(dashboard.average_total_score.toFixed(2))
        : null,
  }
  const userTeam = teams.find((team) => team.members.includes(user?.id || ''))
  const userTeamSummary = userTeam ? getTeamRegistrationSummary(userTeam) : null
  const userTeamIssues = userTeam ? getPriorityRegistrationIssues(userTeam, 5) : []
  const canViewAdminQueue = isAdminQueue
  const teamsNeedingAction = teams.filter((team) => {
    const summary = getTeamRegistrationSummary(team)
    return team.status !== 'deleted' && (!summary.is_complete || team.status === 'registered' || team.status === 'rejected')
  })
  const missingStudentIdCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('missing_student_id')).length
  const missingProfileCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('team_profile')).length
  const incompleteMembersCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('member_count')).length
  const uploadedProfileCount = teams.filter((team) => getTeamRegistrationSummary(team).has_profile_file).length

  // ─── B.1 — Phân tích & Báo cáo (admin) ────────────────────────────────
  // Compute metrics chỉ từ data đã load — tránh thêm endpoint mới.
  const submittedMemorialTeamIds = new Set(
    allMemorials.filter((m: any) => m.team_id && m.is_official_submission).map((m: any) => m.team_id),
  )
  const teamsSubmittedR1Count = teams.filter(
    (t) => t.status !== 'deleted' && submittedMemorialTeamIds.has(t.id),
  ).length
  const teamsMissingDossierCount = teams.filter(
    (t) => t.status !== 'deleted' && !submittedMemorialTeamIds.has(t.id),
  ).length
  const teamsApprovedCount = teams.filter(
    (t) => t.status === 'active' && getTeamRegistrationSummary(t).is_complete,
  ).length
  // C3 (audit 2026-05-22): shortlist count dựa trên team.competition_status thật
  // — đếm các team đã có status `r1_shortlisted_for_review` / `advanced_to_*`.
  // Trước đây dùng leaderboard.score_count > 0 → gây hiểu nhầm "đã có shortlist"
  // khi mới có 1 GK chấm. Giờ chỉ count team có state thực sau publish.
  const shortlistCount = teams.filter((t) =>
    ['r1_shortlisted_for_review', 'advanced_to_semifinal', 'advanced_to_final'].includes(
      (t as any).competition_status || '',
    ),
  ).length
  const advancedTopCount = teams.filter((t) =>
    ['advanced_to_semifinal', 'advanced_to_final'].includes(
      (t as any).competition_status || '',
    ),
  ).length
  // AI scoring status: nhãn trung tính — backend chưa expose state thực, đừng
  // ép thành "Đang chạy" gây hiểu nhầm. Đổi message.
  const aiScoringActive = (dashboard?.submitted_scores_count ?? 0) > 0
  // Cảnh báo deadline: số cuộc thi có deadline trong 48h tới
  const now = new Date()
  const in48h = new Date(now.getTime() + 48 * 3600 * 1000)
  const upcomingDeadlineCount = cases.filter((c) => {
    if (!c.deadline) return false
    const dl = new Date(c.deadline)
    return dl > now && dl < in48h
  }).length

  // ─── B.2 — Student R1 result ──────────────────────────────────────────
  // P2-#22 (audit batch B): defensive — chỉ fetch khi caseId thật sự có giá trị
  // không rỗng (tránh request /public/results//latest với path trống → 404).
  const studentFirstAssignedCaseId =
    isStudent && userTeam ? cases.find((c) => (c.assigned_team_ids || []).includes(userTeam.id))?.id : undefined
  const hasValidStudentCaseId = !!studentFirstAssignedCaseId && studentFirstAssignedCaseId.trim().length > 0
  const { data: latestPublicResult, isFetching: isFetchingPublicResult } = useGetPublicLatestResultsQuery(
    hasValidStudentCaseId ? studentFirstAssignedCaseId! : (undefined as any),
    { skip: !hasValidStudentCaseId },
  )
  const r1Published = !!latestPublicResult?.published_at
  const isUserTeamInTop4 =
    r1Published && userTeam
      ? (latestPublicResult?.advancing_teams || []).some((at) => at.team_name === userTeam.name)
      : false

  // 2026-05-26: BK stage — fetch rounds + bracket cho case của thí sinh để
  // detect publish-match BK (BTC bấm "Công bố ... thắng" trên ReportsPage).
  // Khi cả 2 cặp BK đã publish → switch dashboard sang "Top 2" + thông báo
  // tiếp tục/dừng tương ứng. Poll 15s để tự refresh sau khi BTC publish.
  const { data: studentRoundsAll = [] } = useGetRoundsQuery(undefined, {
    skip: !isStudent || !hasValidStudentCaseId,
    pollingInterval: 15000,
  })
  const bkRoundId = useMemo(() => {
    if (!studentFirstAssignedCaseId) return ''
    const r = studentRoundsAll.find(
      (rr) => rr.case_id === studentFirstAssignedCaseId && rr.round_type === RoundType.SEMIFINAL,
    )
    return r?.id || ''
  }, [studentRoundsAll, studentFirstAssignedCaseId])
  const { data: bkBracket } = useGetLatestBracketQuery(
    { case_id: studentFirstAssignedCaseId || '', round_id: bkRoundId },
    { skip: !isStudent || !studentFirstAssignedCaseId || !bkRoundId, pollingInterval: 15000 },
  )
  const bkMatches = useMemo(() => (bkBracket?.matches || []) as any[], [bkBracket])
  // BK đã có ít nhất 1 cặp publish-match (status='published' hoặc winner set)
  const bkAnyPublished = useMemo(
    () => bkMatches.some((m) => m.status === 'published' || !!m.winner_team_id),
    [bkMatches],
  )
  // BK đã publish hết tất cả các cặp (đủ để khoá round)
  const bkAllPublished = useMemo(
    () => bkMatches.length > 0 && bkMatches.every((m) => m.status === 'published' || !!m.winner_team_id),
    [bkMatches],
  )
  // Danh sách team thắng BK (vào CK) — map sang Team object để lấy university.
  const bkWinnerTeams = useMemo(() => {
    const ids: string[] = []
    for (const m of bkMatches) {
      const wid = m.winner_team_id
      if (wid && !ids.includes(wid)) ids.push(wid)
    }
    return ids.map((tid) => teams.find((t) => t.id === tid)).filter(Boolean) as any[]
  }, [bkMatches, teams])
  // userTeam đã thi BK chưa?
  const userTeamPlayedBk = useMemo(() => {
    if (!userTeam) return false
    return bkMatches.some(
      (m) => m.team_plaintiff_id === userTeam.id || m.team_defendant_id === userTeam.id,
    )
  }, [bkMatches, userTeam])
  // userTeam có thắng cặp BK của họ không?
  const userTeamMatch = useMemo(() => {
    if (!userTeam) return null
    return bkMatches.find(
      (m) => m.team_plaintiff_id === userTeam.id || m.team_defendant_id === userTeam.id,
    ) || null
  }, [bkMatches, userTeam])
  const userTeamMatchPublished = !!(userTeamMatch && (userTeamMatch.status === 'published' || !!userTeamMatch.winner_team_id))
  const isUserTeamInTop2 = !!(userTeam && userTeamMatchPublished && userTeamMatch?.winner_team_id === userTeam.id)

  // 2026-05-27 — CK stage: detect khi trận chung kết đã publish-match (chỉ 1
  // cặp duy nhất ở vòng CK). Khi đó hiển thị một badge to chúc mừng nhà vô
  // địch + á quân, thay cho 2 card BK ở trên.
  const ckRoundId = useMemo(() => {
    if (!studentFirstAssignedCaseId) return ''
    const r = studentRoundsAll.find(
      (rr) => rr.case_id === studentFirstAssignedCaseId && rr.round_type === RoundType.FINAL,
    )
    return r?.id || ''
  }, [studentRoundsAll, studentFirstAssignedCaseId])
  const { data: ckBracket } = useGetLatestBracketQuery(
    { case_id: studentFirstAssignedCaseId || '', round_id: ckRoundId },
    { skip: !isStudent || !studentFirstAssignedCaseId || !ckRoundId, pollingInterval: 15000 },
  )
  const ckMatch = useMemo(
    () => ((ckBracket?.matches || []) as any[]).find(
      (m) => m.status === 'published' || !!m.winner_team_id,
    ) || null,
    [ckBracket],
  )
  const ckPublished = !!ckMatch
  const ckChampion = useMemo(() => {
    if (!ckMatch?.winner_team_id) return null
    return teams.find((t) => t.id === ckMatch.winner_team_id) || null
  }, [ckMatch, teams])
  const ckRunnerUp = useMemo(() => {
    if (!ckMatch?.winner_team_id) return null
    const loserId = [ckMatch.team_plaintiff_id, ckMatch.team_defendant_id].find(
      (tid) => tid && tid !== ckMatch.winner_team_id,
    )
    return loserId ? teams.find((t) => t.id === loserId) || null : null
  }, [ckMatch, teams])
  // Dashboard mode ưu tiên CK > BK > R1.
  const dashboardStage: 'r1' | 'bk' | 'ck' = ckPublished ? 'ck' : bkAnyPublished ? 'bk' : 'r1'

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Quản trị viên',
      secretary: 'Ban Thư ký',
      judge: 'Giám khảo',
      student: 'Thí sinh',
      support: 'Hỗ trợ kỹ thuật',
    }
    return labels[role] || role
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      {/* Welcome — thẻ hero đồng bộ nhận diện */}
      <Box
        sx={(theme) => ({
          mb: 4,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: gradientBrand,
            borderRadius: '3px 0 0 3px',
          },
        })}
      >
        <Box sx={{ pl: { xs: 0.5, sm: 1 } }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.65rem', sm: '2.125rem' }, color: 'primary.main' }}>
            Chào mừng đến với Gender Justice Moot Court
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Xin chào, <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{user.full_name || user.username}</Box>
              </Typography>
              <Chip label={getRoleLabel(user.role)} color="secondary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: '42rem', lineHeight: 1.65 }}>
            Hệ thống quản lý và tổ chức cuộc thi tranh tụng điện tử — giao diện thân thiện, luồng làm việc rõ ràng.
          </Typography>
        </Box>
      </Box>

      {userTeam && userTeamSummary && !userTeamSummary.is_complete && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, alignItems: 'flex-start' }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/teams')}>
              Bổ sung
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Hồ sơ đội "{userTeam.name}" còn thiếu {userTeamSummary.missing_count} mục
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <LinearProgress
              variant="determinate"
              value={(userTeamSummary.required_done / Math.max(1, userTeamSummary.required_total)) * 100}
              color="warning"
              sx={{ maxWidth: 320, height: 7, borderRadius: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {userTeamIssues.map((item) => (
                <Chip key={item.key} label={item.label} color="warning" size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {canViewAdminQueue && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Hàng chờ duyệt hồ sơ đội
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamsNeedingAction.length} đội cần kiểm tra, duyệt hoặc yêu cầu bổ sung.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Thiếu MSSV: ${missingStudentIdCount}`} color={missingStudentIdCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Thiếu hồ sơ đội: ${missingProfileCount}`} color={missingProfileCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Chưa đủ thành viên: ${incompleteMembersCount}`} color={incompleteMembersCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Đã upload profile: ${uploadedProfileCount}`} color="success" variant="outlined" size="small" />
            </Box>
            <Button variant="outlined" size="small" onClick={() => navigate('/teams')} sx={{ textTransform: 'none' }}>
              Mở đội thi
            </Button>
          </Box>
        </Paper>
      )}

      {/* B.2 — Block ưu tiên cho Thí sinh: kết quả Vòng Sơ loại / BK / CK.
          2026-05-27: stage 'ck' (chung kết đã publish) — gộp 2 card thành
          một badge full-width công bố Nhà vô địch + Á quân. KHÔNG cá nhân
          hoá theo userTeam — chỉ hiển thị thông tin chung. */}
      {isStudent && dashboardStage === 'ck' && (
        <Paper
          elevation={0}
          sx={(theme) => ({
            mb: 4,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.warning.main, 0.45)}`,
            boxShadow: `0 16px 40px ${alpha(theme.palette.warning.main, 0.18)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.18)} 0%, ${alpha(theme.palette.background.paper, 1)} 70%)`,
            textAlign: 'center',
          })}
        >
          <EmojiEvents sx={{ fontSize: 64, color: 'warning.main', mb: 1 }} />
          <Typography variant="h4" fontWeight={800} sx={{ color: 'warning.dark', mb: 0.5 }}>
            Vòng Chung kết đã khép lại!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Ban Tổ chức trân trọng công bố kết quả chung cuộc của cuộc thi.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={6} md={5}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: 2.5,
                  borderRadius: 2,
                  border: `2px solid ${theme.palette.warning.main}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.18)} 0%, ${alpha(theme.palette.warning.light, 0.08)} 100%)`,
                })}
              >
                <Typography variant="overline" fontWeight={700} sx={{ color: 'warning.dark', letterSpacing: 1 }}>
                  🏆 NHÀ VÔ ĐỊCH
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color: 'warning.dark' }}>
                  {ckChampion?.name || '—'}
                </Typography>
                {ckChampion?.university && (
                  <Typography variant="body2" color="text.secondary">
                    {ckChampion.university}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: 2.5,
                  borderRadius: 2,
                  border: `2px solid ${alpha(theme.palette.text.secondary, 0.4)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.action.hover, 0.6)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                })}
              >
                <Typography variant="overline" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                  🥈 Á QUÂN
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                  {ckRunnerUp?.name || '—'}
                </Typography>
                {ckRunnerUp?.university && (
                  <Typography variant="body2" color="text.secondary">
                    {ckRunnerUp.university}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/achievements')}
            sx={{ mt: 3, textTransform: 'none', fontWeight: 700 }}
          >
            Xem thành tích & xếp hạng
          </Button>
        </Paper>
      )}

      {isStudent && dashboardStage !== 'ck' && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Block 1: Thông báo kết quả Top 4 */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.12)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
              })}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}
              >
                <EmojiEvents />{' '}
                {dashboardStage === 'bk'
                  ? `Thông báo kết quả Top 2${bkAllPublished ? '' : ' (đang cập nhật)'}`
                  : 'Thông báo kết quả Top 4'}
              </Typography>
              {dashboardStage === 'bk' ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {bkAllPublished
                      ? `Kết quả Vòng Bán kết đã được công bố — ${bkWinnerTeams.length} đội vào Vòng Chung kết.`
                      : `Đang công bố từng cặp — ${bkWinnerTeams.length}/${bkMatches.length} cặp đã có winner.`}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {bkWinnerTeams.slice(0, 2).map((wt, idx) => (
                      <Box
                        key={wt.id}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.25,
                          borderRadius: 1.5,
                          backgroundColor:
                            userTeam && wt.id === userTeam.id
                              ? alpha(theme.palette.secondary.main, 0.16)
                              : alpha(theme.palette.action.hover, 0.4),
                          border:
                            userTeam && wt.id === userTeam.id
                              ? `1px solid ${theme.palette.secondary.main}`
                              : '1px solid transparent',
                        })}
                      >
                        <Chip
                          label={idx + 1}
                          color="secondary"
                          size="small"
                          sx={{ fontWeight: 700, minWidth: 32 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {wt.name}
                          </Typography>
                          {wt.university && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {wt.university}
                            </Typography>
                          )}
                        </Box>
                        {userTeam && wt.id === userTeam.id && (
                          <Chip label="Đội của bạn" size="small" color="secondary" />
                        )}
                      </Box>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    variant="text"
                    color="secondary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/public/results')}
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
                  >
                    Xem chi tiết
                  </Button>
                </Box>
              ) : isFetchingPublicResult ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Đang tải...
                </Typography>
              ) : !r1Published ? (
                <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <HourglassEmpty color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Kết quả Vòng Sơ loại chưa được công bố
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ban Tổ chức sẽ thông báo Top 4 đội vào Vòng Bán kết khi có quyết định chính thức.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {latestPublicResult?.title || 'Kết quả Vòng Sơ loại đã được công bố.'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {(latestPublicResult?.advancing_teams || []).slice(0, 4).map((at, idx) => (
                      <Box
                        key={`${at.team_name}-${idx}`}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.25,
                          borderRadius: 1.5,
                          backgroundColor:
                            userTeam && at.team_name === userTeam.name
                              ? alpha(theme.palette.secondary.main, 0.16)
                              : alpha(theme.palette.action.hover, 0.4),
                          border:
                            userTeam && at.team_name === userTeam.name
                              ? `1px solid ${theme.palette.secondary.main}`
                              : '1px solid transparent',
                        })}
                      >
                        <Chip
                          label={idx + 1}
                          color="secondary"
                          size="small"
                          sx={{ fontWeight: 700, minWidth: 32 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {at.team_name}
                          </Typography>
                          {at.university && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {at.university}
                            </Typography>
                          )}
                        </Box>
                        {userTeam && at.team_name === userTeam.name && (
                          <Chip label="Đội của bạn" size="small" color="secondary" />
                        )}
                      </Box>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    variant="text"
                    color="secondary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/public/results')}
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
                  >
                    Xem chi tiết
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Block 2: Kết quả đội của bạn sau Vòng Sơ loại */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
              })}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}
              >
                <TrendingUp />{' '}
                {dashboardStage === 'bk'
                  ? 'Kết quả đội của bạn sau Vòng Bán kết'
                  : 'Kết quả đội của bạn sau Vòng Sơ loại'}
              </Typography>
              {!userTeam ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }} fontStyle="italic">
                  Bạn chưa được phân vào đội thi nào.
                </Typography>
              ) : dashboardStage === 'bk' ? (
                // 2026-05-26: stage BK — message tuỳ theo userTeam có thi BK + win/lose
                !userTeamPlayedBk ? (
                  <Box sx={{ py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <ErrorOutline color="action" sx={{ fontSize: 28 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Đội bạn không tham gia Vòng Bán kết
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userTeam.name} không nằm trong Top 4 vào Bán kết. Cảm ơn đội đã đồng hành cùng cuộc thi.
                      </Typography>
                    </Box>
                  </Box>
                ) : !userTeamMatchPublished ? (
                  <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <HourglassEmpty color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Đang chờ công bố cặp đấu của bạn
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        BTC đã công bố một số cặp BK. Cặp của {userTeam.name} sẽ có kết quả sớm.
                      </Typography>
                    </Box>
                  </Box>
                ) : isUserTeamInTop2 ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <CheckCircle color="success" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} color="success.main">
                          Chúc mừng! Đội bạn vào Vòng Chung kết
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {userTeam.name} đã chiến thắng cặp Bán kết — sẵn sàng cho Vòng Chung kết.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/courtroom')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Chuẩn bị Vòng Chung kết
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                      <ErrorOutline color="warning" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Rất tiếc! Đội bạn dừng ở Vòng Bán kết
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cảm ơn {userTeam.name} đã thi đấu hết mình ở Vòng Bán kết. Hành trình của
                          đội đến đây là một thành tích đáng tự hào — xem chi tiết điểm + nhận xét
                          GK trong mục Thành tích.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/achievements')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Xem thành tích
                    </Button>
                  </Box>
                )
              ) : isFetchingPublicResult ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Đang tải...
                </Typography>
              ) : !r1Published ? (
                <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <HourglassEmpty color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Chưa có kết quả công bố
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Đội <strong>{userTeam.name}</strong> đang chờ Ban Tổ chức công bố kết quả Vòng Sơ loại.
                      Hãy theo dõi mục Thông báo để không bỏ lỡ.
                    </Typography>
                  </Box>
                </Box>
              ) : isUserTeamInTop4 ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <CheckCircle color="success" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        Chúc mừng! Đội bạn vào Top 4
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userTeam.name} đã vượt qua Vòng Sơ loại — sẵn sàng cho Vòng Bán kết.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/courtroom')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Chuẩn bị Vòng Bán kết
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    <ErrorOutline color="warning" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Đội bạn chưa vào Top 4
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cảm ơn {userTeam.name} đã tham dự Vòng Sơ loại. Bạn vẫn có thể xem kết quả
                        chung và phản hồi của Ban Giám khảo trong mục Thành tích.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/achievements')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Xem thành tích
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Statistics Cards — 2026-05-27: ẩn cho student (không quản lý
          các con số tổng, chỉ xem hành trình đội mình). */}
      {!isStudent && (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              borderTop: '4px solid',
              borderTopColor: 'primary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.primary.main, 0.12)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số cuộc thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {realStats.totalCases}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#144E8C', 0.12), color: 'primary.main', width: 56, height: 56 }}>
                  <Folder />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderTop: '4px solid',
              borderTopColor: 'secondary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.15)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số đội thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {realStats.totalTeams}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#F64183', 0.12), color: 'secondary.main', width: 56, height: 56 }}>
                  <Groups />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'success.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.success.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số thí sinh
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {realStats.totalParticipants}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: 'success.dark', width: 56, height: 56 }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'info.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.info.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Điểm trung bình
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {(realStats.averageScore ?? '—')}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.dark', width: 56, height: 56 }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* 2026-05-27: Student dashboard layout mới — pipeline cuộc thi +
          2 sub-block (truy cập nhanh + thông báo). Admin/secretary/judge
          giữ layout 3 cột cũ (Quick Actions + Phân tích + Countdown/Thông báo). */}
      {isStudent ? (
        <Box>
          {/* Pipeline cuộc thi của student */}
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: { xs: 2.5, sm: 3 },
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp color="secondary" /> Tiến trình cuộc thi
            </Typography>
            {(() => {
              const MAIN_TYPES = [RoundType.MEMORIAL, RoundType.SEMIFINAL, RoundType.FINAL]
              const ORDER: Record<string, number> = {
                [RoundType.MEMORIAL]: 1,
                [RoundType.SEMIFINAL]: 2,
                [RoundType.FINAL]: 3,
              }
              const LABEL: Record<string, string> = {
                [RoundType.MEMORIAL]: 'Vòng Sơ loại',
                [RoundType.SEMIFINAL]: 'Vòng Bán kết',
                [RoundType.FINAL]: 'Vòng Chung kết',
              }
              const studentRounds = studentRoundsAll
                .filter((r) => r.case_id === studentFirstAssignedCaseId && MAIN_TYPES.includes(r.round_type))
                .slice()
                .sort((a, b) => (ORDER[a.round_type] || 99) - (ORDER[b.round_type] || 99))

              if (!studentFirstAssignedCaseId || studentRounds.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                    Chưa có cuộc thi nào đang diễn ra cho đội của bạn.
                  </Typography>
                )
              }
              const currentCase = cases.find((c) => c.id === studentFirstAssignedCaseId)
              const fmt = (iso?: string) => {
                if (!iso) return '—'
                try {
                  return new Date(iso).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
                } catch {
                  return '—'
                }
              }
              return (
                <>
                  {currentCase && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>{currentCase.title}</strong>
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                    {studentRounds.map((r, idx) => {
                      const isCompleted = r.status === 'completed'
                      const isActive = r.status === 'active'
                      const isCancelled = r.status === 'cancelled'
                      const color = isCompleted
                        ? 'success.main'
                        : isActive
                        ? 'primary.main'
                        : isCancelled
                        ? 'text.disabled'
                        : 'text.secondary'
                      const bg = isCompleted
                        ? alpha('#2e7d32', 0.10)
                        : isActive
                        ? alpha('#144E8C', 0.10)
                        : 'transparent'
                      return (
                        <Box key={r.id} sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0, flex: '1 1 0' }}>
                          <Paper
                            variant="outlined"
                            sx={(theme) => ({
                              p: 1.75,
                              minWidth: 180,
                              flex: 1,
                              borderRadius: 2,
                              borderColor: isCompleted ? 'success.main' : isActive ? 'primary.main' : theme.palette.divider,
                              borderWidth: isActive ? 2 : 1,
                              background: bg,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                            })}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {isCompleted ? (
                                <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                              ) : isActive ? (
                                <HourglassEmpty sx={{ fontSize: 18, color: 'primary.main' }} />
                              ) : (
                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid', borderColor: 'text.disabled' }} />
                              )}
                              <Typography variant="body2" fontWeight={700} sx={{ color }}>
                                {LABEL[r.round_type] || r.name}
                              </Typography>
                            </Box>
                            {r.start_date && (
                              <Typography variant="caption" color="text.secondary">
                                Bắt đầu: {fmt(r.start_date)}
                              </Typography>
                            )}
                            {r.end_date && (
                              <Typography variant="caption" color="text.secondary">
                                Kết thúc: {fmt(r.end_date)}
                              </Typography>
                            )}
                            <Chip
                              size="small"
                              label={
                                isCompleted ? 'Đã kết thúc'
                                : isActive ? 'Đang diễn ra'
                                : isCancelled ? 'Đã hủy'
                                : 'Sắp tới'
                              }
                              color={isCompleted ? 'success' : isActive ? 'primary' : 'default'}
                              variant={isActive ? 'filled' : 'outlined'}
                              sx={{ alignSelf: 'flex-start', mt: 0.5, fontWeight: 600 }}
                            />
                          </Paper>
                          {idx < studentRounds.length - 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
                              <ArrowForward sx={{ color: 'text.disabled' }} fontSize="small" />
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                </>
              )
            })()}
          </Paper>

          {/* 2 sub-block: Truy cập nhanh + Thông báo */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
                })}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                  <ArrowForward color="secondary" /> Truy cập nhanh
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<Folder />}
                    onClick={() => navigate('/cases')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                  >
                    Cuộc thi
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<Groups />}
                    onClick={() => navigate('/teams')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                  >
                    Đội thi
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
                })}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                  <Notifications color="secondary" /> Thông báo
                </Typography>
                {recentNotifs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                    Chưa có thông báo nào.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {recentNotifs.slice(0, 2).map((n) => (
                      <ListItem
                        key={n.id}
                        disableGutters
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          py: 1,
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => navigate('/notifications')}
                      >
                        <ListItemAvatar sx={{ minWidth: 36, mt: 0.5 }}>
                          {getActivityIcon(n.metadata?.kind || n.type || 'info')}
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                              {n.title}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {n.message}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button
                  fullWidth
                  variant="text"
                  color="primary"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/notifications')}
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                >
                  Xem thêm
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : (
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <ArrowForward color="secondary" /> Truy cập nhanh
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Folder />}
                onClick={() => navigate('/cases')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Cuộc thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Groups />}
                onClick={() => navigate('/teams')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Đội thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Description />}
                onClick={() => navigate('/memorials')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Vòng Sơ loại
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Assessment />}
                onClick={() => navigate('/scoring')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Chấm điểm
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<VideoCall />}
                onClick={() => navigate('/courtroom')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Phiên toà ảo
              </Button>
              {isAdminQueue && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<BarChart />}
                  onClick={() => navigate('/reports')}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                >
                  Báo cáo và Phân tích
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* B.1 — Block giữa: Phân tích & Báo cáo (admin) / Giới thiệu cuộc thi (student) */}
        <Grid item xs={12} md={4}>
          {isStudent ? (
            <CompetitionIntroCard
              userTeam={userTeam ? { name: userTeam.name, id: userTeam.id } : null}
              cases={cases}
              onNavigateCases={() => navigate('/cases')}
              onNavigateDossier={() => navigate('/dossier')}
            />
          ) : (
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <BarChart color="secondary" /> Phân tích & Báo cáo
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <AnalyticsRow
                  icon={<CheckCircle fontSize="small" color="success" />}
                  label="Đội đã nộp đủ Vòng Sơ loại"
                  value={teamsSubmittedR1Count}
                />
                <AnalyticsRow
                  icon={<ErrorOutline fontSize="small" color="warning" />}
                  label="Đội thiếu hồ sơ"
                  value={teamsMissingDossierCount}
                />
                <AnalyticsRow
                  icon={<SmartToy fontSize="small" sx={{ color: aiScoringActive ? 'info.main' : 'text.secondary' }} />}
                  label="Số phiếu đã gửi"
                  valueText={`${dashboard?.submitted_scores_count ?? 0}`}
                />
                <AnalyticsRow
                  icon={<People fontSize="small" color="primary" />}
                  label="Đội đã duyệt"
                  value={teamsApprovedCount}
                />
                <AnalyticsRow
                  icon={<EmojiEvents fontSize="small" sx={{ color: 'secondary.main' }} />}
                  label="Đội đã shortlist / đã advance"
                  value={shortlistCount}
                />
                <AnalyticsRow
                  icon={<EmojiEvents fontSize="small" sx={{ color: 'warning.main' }} />}
                  label="Đội đã vào Bán kết / Chung kết"
                  value={advancedTopCount}
                />
                <AnalyticsRow
                  icon={<WarningAmber fontSize="small" color="error" />}
                  label="Cảnh báo deadline 48h"
                  value={upcomingDeadlineCount}
                />
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<BarChart />}
                onClick={() => navigate('/reports')}
                sx={{ mt: 2, textTransform: 'none' }}
              >
                Xem báo cáo chi tiết
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Upcoming Courtrooms & Top Teams */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* T-7 countdown widget — phiên Bán kết / Chung kết tiếp theo */}
            <NextHearingCountdown />

            {/* YC8 (audit 2026-05-23): Bỏ widget "Sắp diễn ra" — đã có
                NextHearingCountdown T-7 ở trên rồi. */}

            {/* B.1 — Thông báo (thay block "Top 3 đội thi" cũ) */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <Notifications color="secondary" /> Thông báo
              </Typography>
              {recentNotifs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                  Chưa có thông báo nào.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {recentNotifs.slice(0, 4).map((n) => (
                    <ListItem
                      key={n.id}
                      disableGutters
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => navigate('/notifications')}
                    >
                      <ListItemAvatar sx={{ minWidth: 36, mt: 0.5 }}>
                        {getActivityIcon(n.metadata?.kind || n.type || 'info')}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                            {n.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {n.message}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Button
                fullWidth
                variant="text"
                color="primary"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/notifications')}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
              >
                Xem tất cả
              </Button>
            </Paper>
          </Box>
        </Grid>
      </Grid>
      )}
    </Container>
  )
}
